import {
  BellOutlineIcon,
  CalendarOutlineIcon,
  ChatAltOutlineIcon,
  DocumentOutlineIcon,
  type IconComponent,
  LoginOutlineIcon,
  MailOutlineIcon,
  MinusCircleOutlineIcon,
  PencilOutlineIcon,
  PrinterOutlineIcon,
  RefreshOutlineIcon,
  ScoreOutlineIcon,
  SignOutlineIcon,
  SwitchHorizontalOutlineIcon,
  TrashOutlineIcon,
  UploadOutlineIcon,
  XCircleOutlineIcon,
} from '@fountain/ripple/icon';
import {
  ApplicantActionsActionItemKey,
  ApplicantActionsGroupItemKey,
} from '@fountain/types/actionMenu';

import {
  MoveToNextStageIcon,
  MoveToOpeningIcon,
  MoveToStageIcon,
  ScoreDrawingIcon,
} from 'components/KeptIcons';

export const ICON_MAPPINGS: Partial<
  Record<
    ApplicantActionsActionItemKey | ApplicantActionsGroupItemKey,
    { icon: IconComponent; kept?: true }
  >
> = {
  'data_collection.upload': { icon: UploadOutlineIcon },
  'manage.delete': { icon: TrashOutlineIcon },
  'manage.edit': { icon: PencilOutlineIcon },
  'manage.run_rules': { icon: RefreshOutlineIcon },
  'messaging.email': { icon: MailOutlineIcon },
  'messaging.sms': { icon: ChatAltOutlineIcon },
  'messaging.reactivate_sms': { icon: ChatAltOutlineIcon },
  'worker.worker_profile': { icon: ScoreDrawingIcon, kept: true },
  'worker_experience.profile': { icon: SwitchHorizontalOutlineIcon },
  'messaging.unsubscribe': { icon: MinusCircleOutlineIcon },
  'moving.next_stage': { icon: MoveToNextStageIcon, kept: true },
  'moving.stages': { icon: MoveToStageIcon, kept: true },
  'moving.reject': { icon: XCircleOutlineIcon },
  'print.data': { icon: PrinterOutlineIcon },
  'print.score_cards': { icon: PrinterOutlineIcon },
  'score_cards.score': { icon: ScoreOutlineIcon },
  'score_cards.separate_score': { icon: ScoreOutlineIcon },
  'checkr_canada.run_report': { icon: DocumentOutlineIcon },
  'new_checkr_canada.run_report': { icon: DocumentOutlineIcon },
  'checkr.run_remaining_report': { icon: DocumentOutlineIcon },
  'checkr.run_report': { icon: DocumentOutlineIcon },
  'checkr.send_consent_message': { icon: MailOutlineIcon },
  'doc_sign.cancel': { icon: SignOutlineIcon },
  'doc_sign.remind': { icon: BellOutlineIcon },
  'messaging.reactivate_email': { icon: MailOutlineIcon },
  'messaging.send_automated_message': { icon: MailOutlineIcon },
  'moving.move_to_opening': { icon: MoveToOpeningIcon, kept: true },
  'onfido.resume': { icon: DocumentOutlineIcon },
  'onfido.send_message': { icon: MailOutlineIcon },
  'onfido.void': { icon: MinusCircleOutlineIcon },
  'scheduling.cancel': { icon: CalendarOutlineIcon },
  'scheduling.schedule': { icon: CalendarOutlineIcon },
  'scheduling.send_message': { icon: MailOutlineIcon },
  'schoolkeep.enroll_to_course': { icon: LoginOutlineIcon },
  'hire_go.view': { icon: PencilOutlineIcon },
};

export const EXCLUDED_ITEM_KEYS = new Set(['score_cards.separate_score']);

export const RESUBSCRIBE_EMAIL_START =
  'app/ApplicantActions/RESUBSCRIBE_EMAIL_START';
export const RESUBSCRIBE_EMAIL_SUCCESS =
  'app/ApplicantActions/RESUBSCRIBE_EMAIL_SUCCESS';
export const RESUBSCRIBE_EMAIL_ERROR =
  'app/ApplicantActions/RESUBSCRIBE_EMAIL_ERROR';

export const RESUBSCRIBE_SMS_START =
  'app/ApplicantActions/RESUBSCRIBE_SMS_START';
export const RESUBSCRIBE_SMS_SUCCESS =
  'app/ApplicantActions/RESUBSCRIBE_SMS_SUCCESS';
export const RESUBSCRIBE_SMS_ERROR =
  'app/ApplicantActions/RESUBSCRIBE_SMS_ERROR';
export const CHECKR_SEND_CONSENT_MESSAGE_INIT =
  'app/ApplicantActions/CHECKR_SEND_CONSENT_MESSAGE_INIT';
export const CHECKR_SEND_CONSENT_MESSAGE_SUCCESS =
  'app/ApplicantActions/CHECKR_SEND_CONSENT_MESSAGE_SUCCESS';
export const CHECKR_SEND_CONSENT_MESSAGE_ERROR =
  'app/ApplicantActions/CHECKR_SEND_CONSENT_MESSAGE_ERROR';
export const CHECKR_RUN_REPORT_INIT =
  'app/ApplicantActions/CHECKR_RUN_REPORT_INIT';
export const CHECKR_RUN_REPORT_SUCCESS =
  'app/ApplicantActions/CHECKR_RUN_REPORT_SUCCESS';
export const CHECKR_RUN_REPORT_ERROR =
  'app/ApplicantActions/CHECKR_RUN_REPORT_ERROR';
export const CHECKR_RUN_REMAINING_REPORT_INIT =
  'app/ApplicantActions/CHECKR_RUN_REMAINING_REPORT_INIT';
export const CHECKR_RUN_REMAINING_REPORT_SUCCESS =
  'app/ApplicantActions/CHECKR_RUN_REMAINING_REPORT_SUCCESS';
export const CHECKR_RUN_REMAINING_REPORT_ERROR =
  'app/ApplicantActions/CHECKR_RUN_REMAINING_REPORT_ERROR';
export const CANCEL_AND_RESEND_HELLOSIGN_REQUEST_INIT =
  'app/ApplicantActions/CANCEL_AND_RESEND_HELLOSIGN_REQUEST_INIT';
export const CANCEL_AND_RESEND_HELLOSIGN_REQUEST_SUCCESS =
  'app/ApplicantActions/CANCEL_AND_RESEND_HELLOSIGN_REQUEST_SUCCESS';
export const CANCEL_AND_RESEND_HELLOSIGN_REQUEST_ERROR =
  'app/ApplicantActions/CANCEL_AND_RESEND_HELLOSIGN_REQUEST_ERROR';
export const RESEND_HELLOSIGN_EMAIL_INIT =
  'app/ApplicantActions/RESEND_HELLOSIGN_EMAIL_INIT';
export const RESEND_HELLOSIGN_EMAIL_SUCCESS =
  'app/ApplicantActions/RESEND_HELLOSIGN_EMAIL_SUCCESS';
export const RESEND_HELLOSIGN_EMAIL_ERROR =
  'app/ApplicantActions/RESEND_HELLOSIGN_EMAIL_ERROR';
export const SCHOOLKEEP_ENROLL_INIT =
  'app/ApplicantActions/SCHOOLKEEP_ENROLL_INIT';
export const SCHOOLKEEP_ENROLL_SUCCESS =
  'app/ApplicantActions/SCHOOLKEEP_ENROLL_SUCCESS';
export const SCHOOLKEEP_ENROLL_ERROR =
  'app/ApplicantActions/SCHOOLKEEP_ENROLL_ERROR';
export const SCHEDULING_CANCEL_INIT =
  'app/ApplicantActions/SCHEDULING_CANCEL_INIT';
export const SCHEDULING_CANCEL_SUCCESS =
  'app/ApplicantActions/SCHEDULING_CANCEL_SUCCESS';
export const SCHEDULING_CANCEL_ERROR =
  'app/ApplicantActions/SCHEDULING_CANCEL_ERROR';
export const SCHEDULING_RESEND_SCHEDULER_EMAIL_INIT =
  'app/ApplicantActions/SCHEDULING_RESEND_SCHEDULER_EMAIL_INIT';
export const SCHEDULING_RESEND_SCHEDULER_EMAIL_SUCCESS =
  'app/ApplicantActions/SCHEDULING_RESEND_SCHEDULER_EMAIL_SUCCESS';
export const SCHEDULING_RESEND_SCHEDULER_EMAIL_ERROR =
  'app/ApplicantActions/SCHEDULING_RESEND_SCHEDULER_EMAIL_ERROR';
export const CHECKR_CANADA_RUN_REPORT_INIT =
  'app/ApplicantActions/CHECKR_CANADA_RUN_REPORT_INIT';
export const CHECKR_CANADA_RUN_REPORT_SUCCESS =
  'app/ApplicantActions/CHECKR_CANADA_RUN_REPORT_SUCCESS';
export const CHECKR_CANADA_RUN_REPORT_ERROR =
  'app/ApplicantActions/CHECKR_CANADA_RUN_REPORT_ERROR';
export const ONFIDO_RESUME_INIT = 'app/ApplicantActions/ONFIDO_RESUME_INIT';
export const ONFIDO_RESUME_SUCCESS =
  'app/ApplicantActions/ONFIDO_RESUME_SUCCESS';
export const ONFIDO_RESUME_ERROR = 'app/ApplicantActions/ONFIDO_RESUME_ERROR';
export const ONFIDO_SEND_MESSAGE_INIT =
  'app/ApplicantActions/ONFIDO_SEND_MESSAGE_INIT';
export const ONFIDO_SEND_MESSAGE_SUCCESS =
  'app/ApplicantActions/ONFIDO_SEND_MESSAGE_SUCCESS';
export const ONFIDO_SEND_MESSAGE_ERROR =
  'app/ApplicantActions/ONFIDO_SEND_MESSAGE_ERROR';
export const ONFIDO_VOID_INIT = 'app/ApplicantActions/ONFIDO_VOID_INIT';
export const ONFIDO_VOID_SUCCESS = 'app/ApplicantActions/ONFIDO_VOID_SUCCESS';
export const ONFIDO_VOID_ERROR = 'app/ApplicantActions/ONFIDO_VOID_ERROR';
export const SEND_AUTOMATED_MESSAGE =
  'app/ApplicantActions/SEND_AUTOMATED_MESSAGE';
export const SEND_AUTOMATED_MESSAGE_SUCCESS =
  'app/ApplicantActions/SEND_AUTOMATED_MESSAGE_SUCCESS';
export const SEND_AUTOMATED_MESSAGE_ERROR =
  'app/ApplicantActions/SEND_AUTOMATED_MESSAGE_ERROR';
export const WX_EXPORT_APPLICANT_INIT =
  'app/ApplicantActions/WX_EXPORT_APPLICANT_INIT';
export const WX_EXPORT_APPLICANT_SUCCESS =
  'app/ApplicantActions/WX_EXPORT_APPLICANT_SUCCESS';
export const WX_EXPORT_APPLICANT_ERROR =
  'app/ApplicantActions/WX_EXPORT_APPLICANT_ERROR';
